/**
 * NoteLegalTab.jsx — Muhasebe FAZ 4.8, "Avukat İşlemleri" iç sekmesi.
 *
 * Uç: POST /admin/promissory-notes/legal-transfer  (201)
 * Body: { note_ids, lawyer_id, interest_amount, legal_fees, notes }
 *
 * `debt_amount` GÖNDERİLMEZ — backend seçilen senetlerden toplar (aktör
 * tutarı şişiremesin diye body'den okunmuyor). Aşağıdaki "devredilecek
 * toplam" satırı yalnızca BİLGİ amaçlı bir gösterimdir, form alanı DEĞİLDİR.
 *
 * En az 1 aktif senet seçilmelidir (backend 400 `senet_icra_id_zorunlu`).
 */
import { useMemo, useState } from 'react';
import { Gavel } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useConfirmDialog, LoadingState } from '../../../components/admin';
import { fmtMoneyAccounting } from '../../../utils/money';
import { useLawyers, useLegalTransferPromissoryNotes } from '../../../api/queries/accounting';
import {
  ActionButton,
  Field,
  INPUT_CLASS,
  InlineAlert,
  NotePicker,
  TabIntro,
  noteErrorMessage,
} from './noteUiKit';
import { isActiveNote, sumAmounts } from './promissoryMath';

export default function NoteLegalTab({ userId, notes }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const transfer = useLegalTransferPromissoryNotes(userId);
  const { data: lawyers = [], isLoading: lawyersLoading, isError: lawyersError } = useLawyers();

  const [selectedIds, setSelectedIds] = useState([]);
  const [lawyerId, setLawyerId] = useState('');
  const [interestAmount, setInterestAmount] = useState('');
  const [legalFees, setLegalFees] = useState('');
  const [caseNotes, setCaseNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const activeNotes = useMemo(() => (notes || []).filter((n) => isActiveNote(n.status)), [notes]);
  const selectedNotes = useMemo(
    () => activeNotes.filter((n) => selectedIds.includes(n.note_id)),
    [activeNotes, selectedIds]
  );
  const total = sumAmounts(selectedNotes.map((n) => n.amount));

  const numberInvalid = (v) => v !== '' && (!Number.isFinite(Number(v)) || Number(v) < 0);
  const selectionMissing = selectedIds.length === 0;
  const interestInvalid = numberInvalid(interestAmount);
  const feesInvalid = numberInvalid(legalFees);
  const canSubmit = !selectionMissing && !interestInvalid && !feesInvalid;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;

    const ok = await confirm({
      title: t('accounting.notes.legal.confirm_title'),
      description: `${t('accounting.notes.legal.confirm_description')} ${t('accounting.notes.irreversible')}`,
      variant: 'danger',
      confirmLabel: t('accounting.notes.confirm_continue'),
    });
    if (!ok) return;

    try {
      await transfer.mutateAsync({
        note_ids: selectedIds,
        lawyer_id: lawyerId || null,
        interest_amount: interestAmount === '' ? 0 : Number(interestAmount),
        legal_fees: legalFees === '' ? 0 : Number(legalFees),
        notes: caseNotes,
      });
      toast.success(t('accounting.notes.legal.success'));
      setSelectedIds([]);
      setLawyerId('');
      setInterestAmount('');
      setLegalFees('');
      setCaseNotes('');
      setSubmitted(false);
    } catch (err) {
      toast.error(noteErrorMessage(err, lang, t));
    }
  };

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <TabIntro title={t('accounting.notes.legal.title')} description={t('accounting.notes.legal.description')} />

      <NotePicker
        id="note-legal-notes"
        testId="note-legal-picker"
        notes={activeNotes}
        value={selectedIds}
        onChange={setSelectedIds}
        multiple
        label={t('accounting.notes.legal.select_label')}
        required
        hint={t('accounting.notes.legal.select_hint')}
        emptyText={t('accounting.notes.legal.no_active_notes')}
        error={submitted && selectionMissing ? t('accounting.notes.legal.min_error') : ''}
        lang={lang}
        t={t}
      />

      {selectedNotes.length > 0 && (
        <div className="flex items-center justify-between rounded-xl border-2 border-[var(--border-light)] px-3 py-2 text-xs font-bold">
          <span>{t('accounting.notes.legal.total_label')}</span>
          <span className="tabular-nums" data-testid="note-legal-total">{fmtMoneyAccounting(total, lang)}</span>
        </div>
      )}
      <p className="text-[11px] text-[var(--text-muted)]">{t('accounting.notes.legal.total_hint')}</p>

      {lawyersLoading ? (
        <LoadingState />
      ) : lawyersError ? (
        <InlineAlert variant="warning" testId="note-legal-lawyers-error">
          {t('accounting.notes.legal.lawyers_error')}
        </InlineAlert>
      ) : (
        <Field id="note-legal-lawyer" label={t('accounting.notes.legal.lawyer_label')} hint={t('accounting.notes.legal.lawyer_hint')}>
          {({ id }) => (
            <select
              id={id}
              className={INPUT_CLASS}
              value={lawyerId}
              onChange={(e) => setLawyerId(e.target.value)}
              data-testid="note-legal-lawyer-select"
            >
              <option value="">{t('accounting.notes.legal.lawyer_none')}</option>
              {lawyers.map((l) => (
                <option key={l.lawyer_id} value={l.lawyer_id}>
                  {l.firm ? `${l.name} · ${l.firm}` : l.name}
                </option>
              ))}
            </select>
          )}
        </Field>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field
          id="note-legal-interest"
          label={t('accounting.notes.legal.interest_label')}
          error={interestInvalid ? t('accounting.notes.legal.amount_invalid') : ''}
        >
          {({ id, describedBy }) => (
            <input
              id={id}
              type="number"
              step="0.01"
              min="0"
              className={INPUT_CLASS}
              value={interestAmount}
              aria-invalid={interestInvalid ? true : undefined}
              aria-describedby={describedBy}
              onChange={(e) => setInterestAmount(e.target.value)}
              data-testid="note-legal-interest-input"
            />
          )}
        </Field>

        <Field
          id="note-legal-fees"
          label={t('accounting.notes.legal.fees_label')}
          error={feesInvalid ? t('accounting.notes.legal.amount_invalid') : ''}
        >
          {({ id, describedBy }) => (
            <input
              id={id}
              type="number"
              step="0.01"
              min="0"
              className={INPUT_CLASS}
              value={legalFees}
              aria-invalid={feesInvalid ? true : undefined}
              aria-describedby={describedBy}
              onChange={(e) => setLegalFees(e.target.value)}
              data-testid="note-legal-fees-input"
            />
          )}
        </Field>
      </div>

      <Field id="note-legal-notes" label={t('accounting.notes.legal.notes_label')}>
        {({ id }) => (
          <textarea
            id={id}
            rows={3}
            className={INPUT_CLASS}
            value={caseNotes}
            placeholder={t('accounting.notes.legal.notes_placeholder')}
            onChange={(e) => setCaseNotes(e.target.value)}
            data-testid="note-legal-notes-input"
          />
        )}
      </Field>

      <div className="flex justify-end">
        <ActionButton
          type="submit"
          variant="danger"
          icon={Gavel}
          loading={transfer.isPending}
          disabled={transfer.isPending}
          testId="note-legal-submit"
        >
          {transfer.isPending ? t('accounting.notes.submitting') : t('accounting.notes.legal.submit')}
        </ActionButton>
      </div>
    </form>
  );
}
